'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface FAQItem {
    question: string;
    answer: string;
}

const faqs: FAQItem[] = [
    {
        question: "How does AstikLearn's AI personalization work?",
        answer: "Our AI analyzes your learning patterns, current knowledge gaps, and career goals to create a dynamic curriculum. It continuously adapts based on your progress, suggesting relevant resources and exercises to ensure optimal retention and mastery."
    },
    {
        question: "Can colleges integrate AstikLearn with existing LMS?",
        answer: "Yes, AstikLearn is designed to integrate seamlessly with major Learning Management Systems (LMS) like Canvas, Blackboard, and Moodle. We provide robust APIs and dedicated support to ensure a smooth transition and data synchronization."
    },
    {
        question: "Is there a free trial available for students?",
        answer: "Absolutely! Students can start with a 14-day free trial that gives full access to all courses, projects, and the AI mentor. No credit card is required to sign up."
    },
    {
        question: "How are the certifications verified?",
        answer: "All certifications issued by AstikLearn are secured using blockchain technology, making them immutable and instantly verifiable by employers within seconds. You can share your credentials directly on LinkedIn and other professional networks."
    },
    {
        question: "What kind of support can I expect?",
        answer: "We offer 24/7 support for both students and institutions. Students have access to AI mentors and community forums, while institutional partners receive dedicated account managers and technical priority support."
    }
];

const FAQSection = () => {
    const [openIndex, setOpenIndex] = useState<number | null>(0);

    const toggleAccordion = (index: number) => {
        setOpenIndex(openIndex === index ? null : index);
    };

    return (
        <section className="py-20 px-4 sm:px-6 lg:px-8 relative bg-background">
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-full max-w-7xl pointer-events-none">
                <div className="absolute top-1/4 left-0 w-96 h-96 bg-primary/10 rounded-full blur-3xl opacity-50" />
                <div className="absolute bottom-1/4 right-0 w-96 h-96 bg-accent/10 rounded-full blur-3xl opacity-50" />
            </div>

            <div className="max-w-4xl mx-auto relative z-10">
                <div className="text-center mb-16">
                    <h2 className="text-4xl lg:text-5xl font-bold text-text-primary mb-4">
                        Frequently Asked <span className="gradient-text">Questions</span>
                    </h2>
                    <p className="text-xl text-text-secondary max-w-2xl mx-auto">
                        Find answers to common questions about our platform, features, and pricing.
                    </p>
                </div>

                <div className="space-y-4">
                    {faqs.map((faq, index) => (
                        <div
                            key={index}
                            className="glass glass-border rounded-lg overflow-hidden transition-all duration-300 hover:shadow-glow"
                        >
                            <button
                                onClick={() => toggleAccordion(index)}
                                className="w-full flex items-center justify-between p-6 text-left focus:outline-none focus-ring"
                                aria-expanded={openIndex === index}
                            >
                                <span className={`text-lg font-semibold transition-colors ${openIndex === index ? 'text-primary' : 'text-text-primary'}`}>
                                    {faq.question}
                                </span>
                                <span className={`transform transition-transform duration-300 ${openIndex === index ? 'rotate-180' : 'rotate-0'}`}>
                                    <Icon name="ChevronDownIcon" size={20} variant="outline" className={openIndex === index ? 'text-primary' : 'text-text-secondary'} />
                                </span>
                            </button>
                            <div
                                className={`transition-all duration-300 ease-in-out overflow-hidden ${openIndex === index ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                                    }`}
                            >
                                <div className="p-6 pt-0 text-text-secondary leading-relaxed border-t border-white/5 mx-6 mt-2">
                                    {faq.answer}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
};

export default FAQSection;
